import React, { useState, useEffect } from "react";
import {
  X,
  AlertCircle,
  DollarSign,
  CreditCard,
  Building,
  FileText,
} from "lucide-react";
import refundApi from "../api/refundApi";
import { toast } from "react-toastify";

const RefundRequestModal = ({
  isOpen,
  onClose,
  booking,
  bookingType,
  onRefundCreated,
}) => {
  const [step, setStep] = useState(1); // 1: Calculate, 2: Bank Details, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [refundCalculation, setRefundCalculation] = useState(null);

  const [formData, setFormData] = useState({
    bookingPnr: "",
    bookingType: "",
    cancellationReason: "",
    accountHolderName: "",
    ifscCode: "",
    accountNumber: "",
    bankName: "",
    branchName: "",
    accountType: "SAVINGS",
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when booking changes
  useEffect(() => {
    if (booking && isOpen) {
      // Handle both room bookings (booking_pnr) and restaurant bookings (bookingPnr)
      const pnr = booking.booking_pnr || booking.bookingPnr;
      setFormData((prev) => ({
        ...prev,
        bookingPnr: pnr || "",
        bookingType: bookingType === "hotel" ? "ROOM" : "RESTAURANT",
      }));
      fetchRefundCalculation();
    }
  }, [isOpen, booking, bookingType]);

  const fetchRefundCalculation = async () => {
    if (!booking) {
      console.error("Booking is missing", booking);
      toast.error("Booking details are missing");
      onClose();
      return;
    }

    // Handle both naming conventions
    const bookingPnr = booking.booking_pnr || booking.bookingPnr;

    if (!bookingPnr) {
      console.error("Booking PNR is missing from booking object", booking);
      toast.error("Booking PNR details are missing");
      onClose();
      return;
    }

    setLoading(true);
    try {
      const response = await refundApi.calculateRefund(
        bookingPnr,
        bookingType === "hotel" ? "ROOM" : "RESTAURANT",
      );

      if (response.success) {
        setRefundCalculation(response.data);
      } else {
        toast.error(response.message || "Failed to calculate refund");
      }
    } catch (error) {
      toast.error(
        "Error calculating refund: " + (error.message || "Unknown error"),
      );
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Auto-uppercase IFSC code
    if (name === "ifscCode") {
      value = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateBankDetails = () => {
    const newErrors = {};

    if (!formData.accountHolderName || !formData.accountHolderName.trim()) {
      newErrors.accountHolderName = "Account holder name is required";
    }

    if (!formData.ifscCode || !formData.ifscCode.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    } else {
      // More lenient IFSC validation - allow various formats
      const ifscUpper = formData.ifscCode.toUpperCase().trim();
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscUpper)) {
        newErrors.ifscCode = "Invalid IFSC code format (e.g., SBIN0001234)";
      }
    }

    if (!formData.accountNumber || !formData.accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (!/^[0-9]{9,18}$/.test(formData.accountNumber.trim())) {
      newErrors.accountNumber = "Account number must be 9-18 digits";
    }

    if (!formData.bankName || !formData.bankName.trim()) {
      newErrors.bankName = "Bank name is required";
    }

    if (!formData.cancellationReason || !formData.cancellationReason.trim()) {
      newErrors.cancellationReason = "Cancellation reason is required";
    } else if (formData.cancellationReason.trim().length < 5) {
      newErrors.cancellationReason = "Please provide at least 5 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateBankDetails()) {
      // Show which field has error
      const errorField = Object.keys(errors)[0];
      if (errorField) {
        toast.error(`Error: ${errors[errorField]}`);
      } else {
        toast.error("Please fill all required fields correctly");
      }
      return;
    }

    setLoading(true);
    try {
      // Ensure IFSC is uppercase before sending
      const submissionData = {
        ...formData,
        ifscCode: formData.ifscCode.toUpperCase().trim(),
      };

      const response = await refundApi.createRefundRequest(submissionData);

      if (response && response.success) {
        toast.success(
          "Refund request submitted successfully! We'll process it within 3-5 business days.",
        );
        if (onRefundCreated) {
          onRefundCreated(response.data);
        }
        onClose();
      } else {
        const errorMsg =
          response?.message ||
          response?.error ||
          "Failed to create refund request";
        toast.error(errorMsg);
        console.error("Refund creation error:", response);
      }
    } catch (error) {
      console.error("Error creating refund request:", error);
      // Don't close modal on error - let user retry or fix the issue
      const errorMsg =
        error.message ||
        error.response?.data?.message ||
        "Error submitting refund request";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-fadeIn">
      <div className="bg-white rounded-xl sm:rounded-2xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl animate-slideUp">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-pink-600 px-4 py-3 sm:p-4 flex justify-between items-center shadow-lg">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-1.5 rounded-lg backdrop-blur">
              <DollarSign className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">
                Cancel & Refund
              </h2>
              <p className="text-red-100 text-xs">Step {step} of 3</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-all"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(95vh-80px)] sm:max-h-[calc(90vh-80px)]">
          {loading && step === 1 ? (
            <div className="p-8 sm:p-12 text-center bg-gradient-to-b from-white to-red-50">
              <div className="relative mx-auto w-12 h-12 sm:w-16 sm:h-16">
                <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-3 border-red-200"></div>
                <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-3 border-red-600 absolute top-0 left-0"></div>
              </div>
              <p className="mt-4 text-gray-700 font-medium text-sm sm:text-base">
                Calculating refund...
              </p>
            </div>
          ) : (
            <>
              {/* Step 1: Refund Calculation */}
              {step === 1 && refundCalculation && (
                <div className="p-4 sm:p-6">
                  {/* Refund Policy Alert */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-3 sm:p-4 mb-4 shadow-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle
                        className="text-white flex-shrink-0 mt-0.5"
                        size={18}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white text-sm sm:text-base">
                          Refund Policy
                        </h3>
                        <p className="text-xs sm:text-sm text-blue-50 mt-1 leading-snug">
                          {refundCalculation.refundPolicy}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Compact Refund Details */}
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <FileText className="text-gray-600" size={16} />
                        <span className="text-gray-700 font-medium text-sm">
                          PNR
                        </span>
                      </div>
                      <span className="font-bold text-gray-900 text-sm">
                        {refundCalculation.bookingPnr}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2">
                        <DollarSign className="text-purple-600" size={16} />
                        <span className="text-gray-700 font-medium text-sm">
                          Original
                        </span>
                      </div>
                      <span className="font-bold text-gray-900 text-sm sm:text-base">
                        ₹{refundCalculation.originalAmount?.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 sm:p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2">
                        <CreditCard className="text-white" size={18} />
                        <span className="text-white font-bold text-sm sm:text-base">
                          Refund ({refundCalculation.refundPercentage}%)
                        </span>
                      </div>
                      <span className="font-bold text-white text-lg sm:text-xl">
                        ₹{refundCalculation.refundAmount?.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg shadow-md">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="text-white" size={16} />
                        <span className="text-white font-bold text-sm">
                          Cancellation Fee
                        </span>
                      </div>
                      <span className="font-bold text-white text-sm sm:text-base">
                        ₹{refundCalculation.cancellationFees?.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="text-amber-600" size={16} />
                        <span className="text-gray-700 font-medium text-sm">
                          Time Before
                        </span>
                      </div>
                      <span className="font-bold text-amber-700 text-sm">
                        {refundCalculation.hoursBeforeBooking}h
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-4 sm:mt-6 flex gap-2 sm:gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 py-2.5 sm:py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-all text-sm sm:text-base"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setStep(2)}
                      className="flex-1 py-2.5 sm:py-3 px-4 bg-gradient-to-r from-red-600 to-pink-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-sm sm:text-base"
                      disabled={loading}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Bank Details & Reason */}
              {step === 2 && (
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                      <Building className="text-red-500" size={20} />
                      Bank Details
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Fields with * are required
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* Cancellation Reason */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                        <FileText size={14} className="text-red-500" />
                        Reason *
                      </label>
                      <textarea
                        name="cancellationReason"
                        value={formData.cancellationReason}
                        onChange={handleInputChange}
                        placeholder="Why are you cancelling? (min 10 chars)"
                        className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm ${
                          errors.cancellationReason
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                        }`}
                        rows="2"
                      />
                      {errors.cancellationReason && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.cancellationReason}
                        </p>
                      )}
                    </div>

                    {/* Account Holder Name */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                        <CreditCard size={14} className="text-red-500" />
                        Account Holder *
                      </label>
                      <input
                        type="text"
                        name="accountHolderName"
                        value={formData.accountHolderName}
                        onChange={handleInputChange}
                        placeholder="As per bank records"
                        className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 text-sm ${
                          errors.accountHolderName
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                      {errors.accountHolderName && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.accountHolderName}
                        </p>
                      )}
                    </div>

                    {/* Account Number & IFSC in Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                          <DollarSign size={14} className="text-red-500" />
                          Account No. *
                        </label>
                        <input
                          type="text"
                          name="accountNumber"
                          value={formData.accountNumber}
                          onChange={handleInputChange}
                          placeholder="9-18 digits"
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 text-sm ${
                            errors.accountNumber
                              ? "border-red-500 bg-red-50"
                              : "border-gray-300"
                          }`}
                        />
                        {errors.accountNumber && (
                          <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {errors.accountNumber}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                          <Building size={14} className="text-red-500" />
                          IFSC *
                        </label>
                        <input
                          type="text"
                          name="ifscCode"
                          value={formData.ifscCode}
                          onChange={handleInputChange}
                          placeholder="SBIN0001234"
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 text-sm font-mono ${
                            errors.ifscCode
                              ? "border-red-500 bg-red-50"
                              : "border-gray-300"
                          }`}
                          maxLength="11"
                          style={{ textTransform: "uppercase" }}
                        />
                        {errors.ifscCode && (
                          <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {errors.ifscCode}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bank Name */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                        <Building size={14} className="text-red-500" />
                        Bank Name *
                      </label>
                      <input
                        type="text"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleInputChange}
                        placeholder="e.g., State Bank of India"
                        className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 text-sm ${
                          errors.bankName
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                      {errors.bankName && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.bankName}
                        </p>
                      )}
                    </div>

                    {/* Branch & Account Type */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                          <Building size={14} className="text-gray-500" />
                          Branch
                        </label>
                        <input
                          type="text"
                          name="branchName"
                          value={formData.branchName}
                          onChange={handleInputChange}
                          placeholder="Optional"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                          <CreditCard size={14} className="text-red-500" />
                          Type *
                        </label>
                        <select
                          name="accountType"
                          value={formData.accountType}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="SAVINGS">Savings</option>
                          <option value="CURRENT">Current</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-4 sm:mt-6 flex gap-2 sm:gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 py-2.5 sm:py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-all text-sm sm:text-base"
                      disabled={loading}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={() => {
                        if (validateBankDetails()) {
                          setStep(3);
                        }
                      }}
                      className="flex-1 py-2.5 sm:py-3 px-4 bg-gradient-to-r from-red-600 to-pink-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 text-sm sm:text-base"
                      disabled={loading}
                    >
                      Review →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm */}
              {step === 3 && (
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                      <AlertCircle className="text-green-500" size={20} />
                      Review Request
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Verify all details before submitting
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* Booking Details Card */}
                    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 rounded-lg shadow-lg">
                      <h4 className="font-bold text-white text-sm mb-2 flex items-center gap-1.5">
                        <FileText size={16} />
                        Booking Details
                      </h4>
                      <div className="space-y-1.5 text-xs sm:text-sm">
                        <div className="flex justify-between text-purple-50">
                          <span>PNR:</span>
                          <span className="font-bold text-white">
                            {formData.bookingPnr}
                          </span>
                        </div>
                        <div className="flex justify-between text-purple-50">
                          <span>Refund:</span>
                          <span className="font-bold text-green-300 text-base sm:text-lg">
                            ₹{refundCalculation?.refundAmount?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {refundCalculation?.refundStatus && (
                        <div className="mt-2 pt-2 border-t border-purple-400">
                          <span className="text-xs text-purple-100">
                            Status:{" "}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-white text-xs font-bold ${
                              refundCalculation.refundStatus === "REJECTED"
                                ? "bg-red-500"
                                : refundCalculation.refundStatus === "APPROVED"
                                  ? "bg-green-500"
                                  : refundCalculation.refundStatus === "PENDING"
                                    ? "bg-yellow-500"
                                    : "bg-blue-500"
                            }`}
                          >
                            {refundCalculation.refundStatus}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Rejection Reason if applicable */}
                    {refundCalculation?.refundStatus === "REJECTED" &&
                      refundCalculation?.rejectionReason && (
                        <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-lg p-3 shadow-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle
                              className="text-white flex-shrink-0"
                              size={16}
                            />
                            <div className="text-xs sm:text-sm text-white">
                              <p className="font-bold">Refund Rejected</p>
                              <p className="mt-1 leading-relaxed text-red-50">
                                {refundCalculation.rejectionReason}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Bank Details Card */}
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-600 p-4 rounded-lg shadow-lg">
                      <h4 className="font-bold text-white text-sm mb-2 flex items-center gap-1.5">
                        <Building size={16} />
                        Bank Details
                      </h4>
                      <div className="space-y-1.5 text-xs sm:text-sm">
                        <div className="flex justify-between text-blue-50">
                          <span>Holder:</span>
                          <span className="font-semibold text-white">
                            {formData.accountHolderName}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-50">
                          <span>Account:</span>
                          <span className="font-mono font-semibold text-white">
                            {formData.accountNumber}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-50">
                          <span>IFSC:</span>
                          <span className="font-mono font-semibold text-white">
                            {formData.ifscCode}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-50">
                          <span>Bank:</span>
                          <span className="font-semibold text-white">
                            {formData.bankName}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-50">
                          <span>Type:</span>
                          <span className="font-semibold text-white">
                            {formData.accountType}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Important Information Alert */}
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg p-3 shadow-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle
                          className="text-white flex-shrink-0 mt-0.5"
                          size={16}
                        />
                        <div className="text-xs sm:text-sm text-white">
                          <p className="font-bold mb-1">Important:</p>
                          <ul className="list-disc ml-3 space-y-0.5 leading-relaxed text-amber-50">
                            <li>Refund in 3-5 business days</li>
                            <li>Email notifications sent</li>
                            <li>Action cannot be undone</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-4 sm:mt-6 flex gap-2 sm:gap-3">
                    <button
                      onClick={() => setStep(2)}
                      className="flex-1 py-2.5 sm:py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-all text-sm sm:text-base"
                      disabled={loading}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="flex-1 py-2.5 sm:py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 text-sm sm:text-base"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span className="text-xs sm:text-sm">
                            Submitting...
                          </span>
                        </span>
                      ) : (
                        "✓ Confirm & Submit"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundRequestModal;
